import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/apiClient';
import { getAdminToken } from '../utils/auth';
import { REBSTOCK_BOT_SLUG, AIAPPSPACE_BOT_SLUG } from '../api/aiLearningApi';

function withAiAppSpaceApp(apps) {
  const list = Array.isArray(apps) ? [...apps] : [];
  const defaults = {
    slug: AIAPPSPACE_BOT_SLUG,
    name: 'AiAppSpace Bot',
    color: '#5b4fcf',
    synthetic: true,
  };
  const idx = list.findIndex((a) => a.slug === AIAPPSPACE_BOT_SLUG);
  if (idx === -1) {
    return [...list, defaults];
  }
  list[idx] = { ...defaults, ...list[idx], name: 'AiAppSpace Bot', synthetic: false };
  return list;
}

function withHotelRebstockApp(apps) {
  const list = Array.isArray(apps) ? [...apps] : [];
  const defaults = {
    slug: REBSTOCK_BOT_SLUG,
    name: 'Hotel Rebstock',
    color: '#3e7b6f',
    synthetic: true,
  };
  const idx = list.findIndex((a) => a.slug === REBSTOCK_BOT_SLUG);
  if (idx === -1) {
    return [defaults, ...list];
  }
  list[idx] = { ...defaults, ...list[idx], name: 'Hotel Rebstock', synthetic: false };
  return list;
}


const AppContext = createContext({
  apps: [],
  selectedApp: null,
  setSelectedApp: () => {},
  reloadApps: () => {},
  appsLoading: false,
  firebaseProjectFilter: null,
  setFirebaseProjectFilter: () => {},
  firebaseProjectIds: [],
  projectsLoading: false,
});

export function AppProvider({ children }) {
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appsLoading, setAppsLoading] = useState(false);

  // Global Firebase project filter — persisted across page reloads
  const [firebaseProjectFilter, _setFirebaseProjectFilter] = useState(
    () => localStorage.getItem('firebaseProjectFilter') || null
  );

  // Firebase projects fetched from backend (authoritative source)
  const [firebaseProjectIds, setFirebaseProjectIds] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const reloadFirebaseProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const res = await api.get('/api/admin/apps/firebase-projects');
      setFirebaseProjectIds(res.data?.firebaseProjects || []);
    } catch {
      // silently fail — endpoint may be unreachable
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const reloadApps = useCallback(async () => {
    if (!getAdminToken()) return;
    setAppsLoading(true);
    try {
      const res = await api.get('/api/admin/apps');
      const list = withAiAppSpaceApp(withHotelRebstockApp(res.data?.apps || []));
      setApps(list);
      const savedSlug = localStorage.getItem('selectedAppSlug');
      if (savedSlug) {
        const found = list.find((a) => a.slug === savedSlug);
        setSelectedApp(found || null);
      }
    } catch {
      // silently fail — auth may not be set yet
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadFirebaseProjects();
    reloadApps();
  }, [reloadFirebaseProjects, reloadApps]);

  const handleSetSelectedApp = (app) => {
    setSelectedApp(app);
    if (app) {
      localStorage.setItem('selectedAppSlug', app.slug);
    } else {
      localStorage.removeItem('selectedAppSlug');
    }
  };

  function setFirebaseProjectFilter(pid) {
    const value = pid || null;
    _setFirebaseProjectFilter(value);
    if (value) {
      localStorage.setItem('firebaseProjectFilter', value);
    } else {
      localStorage.removeItem('firebaseProjectFilter');
    }
  }

  return (
    <AppContext.Provider
      value={{
        apps,
        selectedApp,
        setSelectedApp: handleSetSelectedApp,
        reloadApps,
        appsLoading,
        firebaseProjectFilter,
        setFirebaseProjectFilter,
        firebaseProjectIds,
        projectsLoading,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  return useContext(AppContext);
}
